import React, { useEffect, useMemo, useRef } from 'react'
import {
  Box, Button, Checkbox, IconButton, MenuItem, Select, Skeleton, Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { AGE, C, controlSx, tnum } from './tokens'
import { COLUMNS } from './model'
import OrderRow from './OrderRow'
import OrderCard from './OrderCard'

// Fixed-width columns keep every row on one grid. Restaurant takes a capped
// share of the rest and Customer gets whatever is left; both truncate.
const COL_WIDTH = { age: 124, order: 116, restaurant: '12%', status: 144, eta: 172, delivery: 160, customer: undefined, amount: 88 }

export function BlankState({ icon, title, body, action }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, px: 3, py: 8, textAlign: 'center' }}>
      <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: C.mutedBg, color: C.muted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {icon || <InboxOutlinedIcon fontSize="small" />}
      </Box>
      <Box sx={{ maxWidth: 380 }}>
        <Box sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>{title}</Box>
        <Box sx={{ mt: 0.5, fontSize: 13, color: C.muted }}>{body}</Box>
      </Box>
      {action}
    </Box>
  )
}

export function ErrorState({ onRetry, message }) {
  return (
    <BlankState
      icon={<CloudOffOutlinedIcon fontSize="small" />}
      title="Orders didn't load"
      body={`The dispatch query failed${message ? ` (${message})` : ''}. Your filters are kept — try again, and if it keeps failing, check the backend on Railway.`}
      action={
        <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={onRetry} sx={controlSx}>
          Try again
        </Button>
      }
    />
  )
}

function SkeletonRows({ columns, density }) {
  return Array.from({ length: 10 }, (_, i) => (
    <TableRow key={i} sx={{ height: density === 'compact' ? 40 : 56 }} aria-hidden>
      <TableCell sx={{ pl: 2, pr: 0, borderBottom: `1px solid ${C.borderSoft}` }}>
        <Skeleton variant="rounded" width={16} height={16} />
      </TableCell>
      {columns.map(c => (
        <TableCell key={c} sx={{ px: 1.25, borderBottom: `1px solid ${C.borderSoft}`, opacity: 1 - i * 0.07 }}>
          <Skeleton variant="rounded" height={12} width={c === 'customer' || c === 'restaurant' ? '70%' : c === 'eta' ? 140 : 64} sx={c === 'amount' ? { ml: 'auto' } : null} />
          {density === 'comfortable' && ['order', 'customer', 'amount'].includes(c) && (
            <Skeleton variant="rounded" height={9} width={c === 'amount' ? 40 : '45%'} sx={{ mt: 0.75, ...(c === 'amount' ? { ml: 'auto' } : null) }} />
          )}
        </TableCell>
      ))}
      <TableCell sx={{ borderBottom: `1px solid ${C.borderSoft}` }} />
    </TableRow>
  ))
}

function SkeletonCards() {
  return Array.from({ length: 6 }, (_, i) => (
    <Box key={i} sx={{ display: 'flex', gap: 1.5, px: 2, py: 1.5, borderBottom: `1px solid ${C.borderSoft}`, opacity: 1 - i * 0.1 }} aria-hidden>
      <Skeleton variant="rounded" width={16} height={16} sx={{ mt: 0.5 }} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="rounded" height={18} width="60%" />
        <Skeleton variant="rounded" height={12} width="45%" sx={{ mt: 1 }} />
        <Skeleton variant="rounded" height={12} width="70%" sx={{ mt: 1 }} />
      </Box>
    </Box>
  ))
}

function StaleDivider({ count, onHide }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, bgcolor: C.subtle, borderTop: `1px solid ${C.borderSoft}`, borderBottom: `1px solid ${C.borderSoft}`, fontSize: 12, color: C.muted }}>
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: AGE.stale.bar, flexShrink: 0 }} />
      <span>
        <Box component="span" sx={{ fontWeight: 600, color: C.text }}>{count} stale</Box> — still open more than 24 hours after they were placed. Close them out or hide them.
      </span>
      <Button
        size="small"
        onClick={onHide}
        startIcon={<VisibilityOffOutlinedIcon sx={{ fontSize: '15px !important' }} />}
        sx={{ ml: 'auto', flexShrink: 0, height: 26, textTransform: 'none', fontSize: 12, fontWeight: 500, color: C.text, borderRadius: '6px', '&:hover': { bgcolor: C.mutedBg } }}
      >
        Hide stale
      </Button>
    </Box>
  )
}

function HeaderCell({ column, sort, onSort }) {
  const active = sort.key === column
  const label = COLUMNS.find(c => c.id === column).label
  const Icon = active ? (sort.dir === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon) : UnfoldMoreIcon
  const right = column === 'amount'
  return (
    <TableCell
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      sx={{ px: 1.25, py: 0, height: 40, bgcolor: C.card, borderBottom: `1px solid ${C.border}`, textAlign: right ? 'right' : 'left' }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => onSort(column)}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5, flexDirection: right ? 'row-reverse' : 'row',
          border: 0, bgcolor: 'transparent', p: '2px 4px', mx: '-4px', borderRadius: '4px', cursor: 'pointer',
          font: 'inherit', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', color: active ? C.text : C.muted,
          '& svg': { fontSize: 14, opacity: active ? 1 : 0, transition: 'opacity 120ms' },
          '&:hover': { color: C.text, '& svg': { opacity: active ? 1 : 0.6 } },
          '&:focus-visible': { outline: `2px solid ${C.input}`, '& svg': { opacity: 0.6 } },
        }}
      >
        {label}
        <Icon />
      </Box>
    </TableCell>
  )
}

function Pagination({ total, page, pageSize, onPage, onPageSize }) {
  const pages = Math.max(1, Math.ceil(total / pageSize))
  const from = page * pageSize + 1
  const to = Math.min(total, (page + 1) * pageSize)
  const navSx = { width: 28, height: 28, border: `1px solid ${C.border}`, borderRadius: '8px', color: C.text }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, px: 2, py: 1, borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.muted, ...tnum }}>
      <span><Box component="span" sx={{ fontWeight: 600, color: C.text }}>{from}–{to}</Box> of {total}</span>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Rows</Box>
        <Select
          size="small"
          value={pageSize}
          onChange={e => onPageSize(Number(e.target.value))}
          inputProps={{ 'aria-label': 'Rows per page' }}
          sx={{ height: 28, fontSize: 12, borderRadius: '8px', '& .MuiSelect-select': { py: 0.25, color: C.text } }}
        >
          {[25, 50, 100].map(n => <MenuItem key={n} value={n} sx={{ fontSize: 13 }}>{n}</MenuItem>)}
        </Select>
        <span>{page + 1} / {pages}</span>
        <IconButton size="small" disabled={page === 0} onClick={() => onPage(page - 1)} aria-label="Previous page" sx={navSx}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" disabled={page >= pages - 1} onClick={() => onPage(page + 1)} aria-label="Next page" sx={navSx}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  )
}

export default function DispatchTable(props) {
  const {
    rows, loading, error, onRetry, density, columns, sort, onSort, selectedIds, onToggle, onToggleMany,
    hoveredId, onHover, page, pageSize, onPage, onPageSize, staleStart, staleCount, onHideStale, empty, splitView,
  } = props
  const scrollRef = useRef(null)
  const start = page * pageSize
  const pageRows = rows.slice(start, start + pageSize)
  const pageIds = pageRows.map(v => v.id)
  const selectedOnPage = pageIds.filter(id => selectedIds.has(id)).length
  const allOnPage = pageIds.length > 0 && selectedOnPage === pageIds.length
  const columnSet = useMemo(() => new Set(columns), [columns])
  const colCount = columns.length + 2
  const blank = !loading && !error && rows.length === 0

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }, [page, pageSize])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: { lg: 1 }, minHeight: { lg: 0 } }}>
      <Box ref={scrollRef} sx={{ flex: { lg: 1 }, minHeight: { lg: 0 }, overflow: { lg: 'auto' } }}>
        {/* Desktop table */}
        <Table
          stickyHeader
          size="small"
          sx={{ display: { xs: 'none', lg: 'table' }, tableLayout: 'fixed', width: '100%', minWidth: splitView ? 720 : 1040, borderCollapse: 'separate' }}
        >
          <colgroup>
            <col style={{ width: 40 }} />
            {columns.map(c => <col key={c} style={COL_WIDTH[c] ? { width: COL_WIDTH[c] } : undefined} />)}
            <col style={{ width: 40 }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell sx={{ pl: 1.5, pr: 0, py: 0, height: 40, bgcolor: C.card, borderBottom: `1px solid ${C.border}` }}>
                <Checkbox
                  size="small"
                  checked={allOnPage}
                  indeterminate={selectedOnPage > 0 && !allOnPage}
                  disabled={pageIds.length === 0}
                  onChange={() => onToggleMany(pageIds, !allOnPage)}
                  inputProps={{ 'aria-label': 'Select all orders on this page' }}
                  sx={{ p: 0.5, color: C.input, '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: C.ink } }}
                />
              </TableCell>
              {columns.map(c => <HeaderCell key={c} column={c} sort={sort} onSort={onSort} />)}
              <TableCell sx={{ bgcolor: C.card, borderBottom: `1px solid ${C.border}` }}>
                <Box component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Actions</Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <SkeletonRows columns={columns} density={density} />
            ) : error ? (
              <TableRow><TableCell colSpan={colCount} sx={{ border: 0 }}><ErrorState onRetry={onRetry} message={error} /></TableCell></TableRow>
            ) : blank ? (
              <TableRow><TableCell colSpan={colCount} sx={{ border: 0 }}>{empty}</TableCell></TableRow>
            ) : (
              pageRows.map((v, i) => (
                <React.Fragment key={v.id}>
                  {staleStart != null && start + i === staleStart && (
                    <TableRow>
                      <TableCell colSpan={colCount} sx={{ p: 0, border: 0 }}>
                        <StaleDivider count={staleCount} onHide={onHideStale} />
                      </TableCell>
                    </TableRow>
                  )}
                  <OrderRow
                    view={v}
                    columns={columnSet}
                    density={density}
                    selected={selectedIds.has(v.id)}
                    highlighted={hoveredId === v.id}
                    onToggle={onToggle}
                    onHover={onHover}
                  />
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>

        {/* Phone & tablet cards */}
        <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
          {loading ? (
            <SkeletonCards />
          ) : error ? (
            <ErrorState onRetry={onRetry} message={error} />
          ) : blank ? (
            empty
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, gap: '1px', bgcolor: C.borderSoft }}>
              {pageRows.map((v, i) => (
                <React.Fragment key={v.id}>
                  {staleStart != null && start + i === staleStart && (
                    <Box sx={{ gridColumn: '1 / -1' }}><StaleDivider count={staleCount} onHide={onHideStale} /></Box>
                  )}
                  <OrderCard view={v} selected={selectedIds.has(v.id)} onToggle={onToggle} />
                </React.Fragment>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {!loading && !error && rows.length > 0 && (
        <Pagination total={rows.length} page={page} pageSize={pageSize} onPage={onPage} onPageSize={onPageSize} />
      )}
    </Box>
  )
}
